import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cx } from "./utils";
import { IconButton } from "./IconButton";
import { Icon } from "./Icon";
import type { Tone } from "./Badge";

/** A single toast message. */
export interface ToastItem {
  id: string;
  message: ReactNode;
  /** @default "neutral" */
  tone?: Tone;
  /** Auto-dismiss delay in ms; 0 keeps it until dismissed. @default 4000 */
  durationMs?: number;
}

export interface ToastApi {
  /** Show a toast; returns its id. */
  show: (message: ReactNode, opts?: { tone?: Tone; durationMs?: number }) => string;
  /** Convenience wrappers. */
  success: (message: ReactNode) => string;
  error: (message: ReactNode) => string;
  info: (message: ReactNode) => string;
  /** Dismiss by id. */
  dismiss: (id: string) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

let n = 0;

/** Renders the toast region and provides `useToast` to the subtree. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const show = useCallback<ToastApi["show"]>(
    (message, opts) => {
      n += 1;
      const id = `toast-${n}`;
      const durationMs = opts?.durationMs ?? 4000;
      setItems((prev) => [...prev, { id, message, tone: opts?.tone ?? "neutral", durationMs }]);
      if (durationMs > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), durationMs),
        );
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (m) => show(m, { tone: "success" }),
      error: (m) => show(m, { tone: "danger", durationMs: 6000 }),
      info: (m) => show(m, { tone: "info" }),
    }),
    [show, dismiss],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="xz-toasts" role="region" aria-label="通知">
        {items.map((t) => (
          <div
            key={t.id}
            className={cx("xz-toast", `xz-toast--${t.tone ?? "neutral"}`)}
            role="status"
            aria-live="polite"
          >
            <span className="xz-toast__msg">{t.message}</span>
            <IconButton
              icon={<Icon id="ui-close" size={16} />}
              label="关闭通知"
              size="sm"
              onClick={() => dismiss(t.id)}
            />
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/** Access the toast API. Requires a `ToastProvider` ancestor. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast 必须在 <ToastProvider> 内使用");
  return ctx;
}
